import { useState, useEffect } from 'react';
import { useAuthStore } from './stores/authStore';
import { useTheme } from './hooks/useTheme';
import { useToasts } from './hooks/useToast';
import { LoginScreen } from './components/LoginScreen';
import { Sidebar } from './components/Sidebar';
import { SettingsModal } from './components/SettingsModal';
import { GlobalSearch } from './components/GlobalSearch';
import { ToastContainer } from './components/ToastContainer';
import { DashboardView } from './components/dashboard/DashboardView';
import { NotesView } from './components/notes/NotesView';
import { TasksView } from './components/tasks/TasksView';
import { BudgetView } from './components/budget/BudgetView';
import { LayoutDashboard, FileText, CheckSquare, Wallet, Menu, RefreshCw } from 'lucide-react';

type TabId = 'dashboard' | 'notes' | 'tasks' | 'budget';

export default function App() {
  const { isAuthenticated, loadFromStorage } = useAuthStore();
  const [activeTab, setActiveTab] = useState<TabId>('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedNoteId, setSelectedNoteId] = useState<number | null>(null);
  const [noteAction, setNoteAction] = useState<'create' | 'save' | null>(null);
  const [taskAction, setTaskAction] = useState<'create' | null>(null);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const { toasts, removeToast } = useToasts();
  useTheme(); // apply saved theme

  useEffect(() => { loadFromStorage(); }, [loadFromStorage]);

  // Global hotkeys
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!isAuthenticated) return;
      const tag = (e.target as HTMLElement).tagName;
      const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';

      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case '1': e.preventDefault(); setActiveTab('dashboard'); break;
          case '2': e.preventDefault(); setActiveTab('notes'); break;
          case '3': e.preventDefault(); setActiveTab('tasks'); break;
          case '4': e.preventDefault(); setActiveTab('budget'); break;
          case 'k':
            e.preventDefault();
            setShowSearch(v => !v);
            break;
          case 'n':
            e.preventDefault();
            if (!e.shiftKey) { setActiveTab('notes'); setNoteAction('create'); }
            else { setActiveTab('tasks'); setTaskAction('create'); }
            break;
          case 's':
            e.preventDefault();
            if (!e.shiftKey) setNoteAction('save');
            break;
        }
      }

      if (!isInput && !e.ctrlKey && !e.metaKey) {
        if (e.key === '?') setShowShortcuts(v => !v);
        if (e.key === 'Escape') setShowShortcuts(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <>
        <LoginScreen />
        <ToastContainer toasts={toasts} onRemove={removeToast} />
      </>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-950">
      <Sidebar
        activeTab={activeTab} onTabChange={setActiveTab}
        onOpenSettings={() => setShowSettings(true)} onOpenSearch={() => setShowSearch(true)}
        isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)}
      />
      <main className="flex-1 overflow-hidden flex flex-col min-w-0">
        {/* Mobile header */}
        <div className="md:hidden flex items-center gap-3 px-4 py-3 border-b border-slate-700 bg-slate-900 shrink-0">
          <button onClick={() => setSidebarOpen(true)} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors">
            <Menu size={20} />
          </button>
          <span className="font-semibold text-white text-sm flex-1">Daily Lab</span>
          <button onClick={() => window.dispatchEvent(new CustomEvent('app:refresh'))} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors" title="Обновить">
            <RefreshCw size={16} />
          </button>
        </div>
        {/* View content */}
        <div className="flex-1 overflow-hidden pb-14 md:pb-0">
          {activeTab === 'dashboard' && <DashboardView onNavigate={setActiveTab} />}
          {activeTab === 'notes' && <NotesView initialNoteId={selectedNoteId} onNoteOpened={() => setSelectedNoteId(null)} pendingAction={noteAction} onActionHandled={() => setNoteAction(null)} />}
          {activeTab === 'tasks' && <TasksView onNavigateToNote={(noteId) => { setSelectedNoteId(noteId); setActiveTab('notes'); }} pendingAction={taskAction} onActionHandled={() => setTaskAction(null)} />}
          {activeTab === 'budget' && <BudgetView onNavigateToNote={(noteId) => { setSelectedNoteId(noteId); setActiveTab('notes'); }} />}
        </div>
        {/* Mobile bottom navigation */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-30 flex bg-slate-900 border-t border-slate-700">
          {([
            { id: 'dashboard' as TabId, label: 'Главная', icon: LayoutDashboard },
            { id: 'notes' as TabId,     label: 'Заметки', icon: FileText },
            { id: 'tasks' as TabId,     label: 'Задачи',  icon: CheckSquare },
            { id: 'budget' as TabId,    label: 'Бюджет',  icon: Wallet },
          ] as { id: TabId; label: string; icon: typeof FileText }[]).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-xs transition-colors ${
                activeTab === id ? 'text-indigo-400' : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              <Icon size={20} />
              <span className="text-[10px]">{label}</span>
            </button>
          ))}
        </nav>
      </main>
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
      <GlobalSearch
        isOpen={showSearch}
        onClose={() => setShowSearch(false)}
        onNavigate={(tab, _id) => { setActiveTab(tab); setShowSearch(false); }}
      />
      {showShortcuts && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowShortcuts(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-slate-900 border border-slate-700 rounded-2xl p-6 w-80 shadow-2xl animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-slate-300 font-semibold text-base mb-4">Горячие клавиши</h2>
            <div className="space-y-2.5">
              {([
                ['Ctrl+K', 'Глобальный поиск'],
                ['Ctrl+1 / 2 / 3 / 4', 'Переключение вкладок'],
                ['Ctrl+N', 'Новая заметка'],
                ['Ctrl+Shift+N', 'Новая задача'],
                ['Ctrl+S', 'Сохранить заметку'],
                ['Esc', 'Закрыть модалку задачи'],
                ['?', 'Эта справка'],
              ] as [string, string][]).map(([key, desc]) => (
                <div key={key} className="flex items-center justify-between gap-4">
                  <kbd className="text-xs bg-slate-800 border border-slate-600 px-2 py-1 rounded-lg text-slate-300 font-mono whitespace-nowrap">{key}</kbd>
                  <span className="text-sm text-slate-400 text-right">{desc}</span>
                </div>
              ))}
            </div>
            <button onClick={() => setShowShortcuts(false)} className="mt-5 w-full py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm rounded-xl transition-colors">Закрыть</button>
          </div>
        </div>
      )}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
